<script setup lang="ts">
import { fakes } from "@/fakes";
import { calculateInvoiceNumbers, runWhen } from "@/shared";
import { PV } from "@/widgets";
import { computed, ref, watch } from "vue";
import BasketAddIcon from "~icons/hugeicons/shopping-basket-add-01";
import TrashIcon from "~icons/mdi/trash-outline";
import BasketIcon from "~icons/ph/basket";
import { assertUnhandledCase } from "../../common/asserts.common";
import { formatRupiah } from "../../common/formats.common";
import type { InvoiceItem } from "../../common/types.common";
import { requireds, v } from "../../common/validation.common";
import { discountOptions, taxOptions } from "../../data/options";
import { useSettingsStore } from "../../stores/settings.store";
import { defineActions, defineState } from "../common";
import { FormController } from "../controller";
import { FormInputNumeric, FormInputSelect, FormInputText } from "../input";
import { FormHook, FormMessage, FormPanel, FormTool } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = InvoiceItem[];
interface PropsType extends BaseProps<ModelType> {
    guideText?: string;
    discount: number | undefined;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    value: undefined,
});

const model = defineModel<ModelType>({
    required: true,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state);
const invoiceNumbers = computed(() =>
    calculateInvoiceNumbers(
        model.value,
        runWhen(props.discount, (discount) => [{ name: "Diskon Tagihan", cost: discount }]),
    ),
);

const settings = useSettingsStore();
const taxType = ref<(typeof taxOptions)[number]["value"]>("none");
const discountType = ref<(typeof discountOptions)[number]["value"]>("none");

const maxDiscount = computed(() => {
    if (discountType.value === "percentage") return 100;
    return undefined;
});

const discountPrefix = computed(() => {
    switch (discountType.value) {
        case "currency":
            return "Rp. ";
        case "none":
        case "percentage":
            return undefined;
        default:
            return assertUnhandledCase(discountType.value);
    }
});

const discountSuffix = computed(() => {
    switch (discountType.value) {
        case "none":
        case "currency":
            return undefined;
        case "percentage":
            return " %";
        default:
            return assertUnhandledCase(discountType.value);
    }
});

const form = new FormController<Partial<ModelType[number]>>({
    name: undefined,
    price: undefined,
    discount: undefined,
    quantity: undefined,
    tax: undefined,
});

const validators = form.validators({
    name: requireds.string,
    price: requireds.number,
    quantity: requireds.number,
    tax: requireds.number,
});

form.onSubmit(async () => {
    if (await form.isNotValid()) {
        return;
    }

    const data = await v.parseAsync(validators.schema, form.models);
    let discount = form.models.discount;

    switch (discountType.value) {
        case "none":
            discount = 0;
            break;
        case "currency":
            discount = discount ?? 0;
            break;
        case "percentage":
            discount = ((discount ?? 0) / 100.0) * (data.price * data.quantity);
            break;
    }

    if (discount > data.price * data.quantity) {
        discount = data.price * data.quantity;
    }

    model.value.push({
        name: data.name,
        price: data.price,
        discount: discount,
        quantity: data.quantity,
        tax: data.tax,
        total: data.price * data.quantity,
    });

    form.reset();
});

watch(
    taxType,
    (v) => {
        switch (v) {
            case "none":
                form.models.tax = 0;
                break;
            case "ppn":
                form.models.tax = settings.data?.ppn ?? 11;
                break;
            default:
                assertUnhandledCase(v);
        }
    },
    { immediate: true },
);

watch(discountType, () => (form.models.discount = 0), { immediate: true });

function onRemove(index: number) {
    model.value.splice(index, 1);
}
</script>

<template>
    <FormPanel :param="props">
        <template #header>
            <BasketIcon />
            <div class="font-semibold text-gray-600">
                {{ props.label ?? "Produk & Jasa" }}
            </div>
        </template>

        <template #icons>
            <span class="inline-flex flex-row">
                <FormTool :actions="actions" :param="props" :showUpdate="true" :state="state" />
            </span>
        </template>

        <FormHook v-if="state.editMode" @submit="form.submit">
            <template #input="{ formId }">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormInputText
                        v-model="form.models.name"
                        :attachReset="form.attachReset()"
                        :attachScramble="form.attachScramble(fakes.product.name)"
                        :attachValidate="form.attachValidate(validators.fields.name)"
                        embedMode="embed"
                        :formId="formId"
                        label="Nama Produk"
                        required
                    />
                    <FormInputNumeric
                        v-model="form.models.price"
                        :attachReset="form.attachReset()"
                        :attachScramble="form.attachScramble(fakes.product.price)"
                        :attachValidate="form.attachValidate(validators.fields.price)"
                        embedMode="embed"
                        :formId="formId"
                        :formSubmit="true"
                        label="Harga Satuan"
                        prefix="Rp. "
                        required
                    />
                    <FormInputNumeric
                        v-model="form.models.quantity"
                        :attachReset="form.attachReset()"
                        :attachScramble="form.attachScramble(fakes.random.number)"
                        :attachValidate="form.attachValidate(validators.fields.quantity)"
                        embedMode="embed"
                        :formId="formId"
                        :formSubmit="true"
                        label="Jumlah"
                        required
                    />
                    <FormInputSelect
                        v-model="taxType"
                        :attachReset="form.attachReset('none' as const)"
                        embedMode="embed"
                        :formId="formId"
                        label="Jenis Pajak"
                        :options="[...taxOptions]"
                        required
                    />
                    <FormInputSelect
                        v-model="discountType"
                        :attachReset="form.attachReset('none' as const)"
                        embedMode="embed"
                        :formId="formId"
                        label="Diskon"
                        :options="[...discountOptions]"
                    />
                    <FormInputNumeric
                        v-show="discountType !== 'none'"
                        v-model="form.models.discount"
                        :attachReset="form.attachReset(0)"
                        embedMode="embed"
                        :formId="formId"
                        :formSubmit="true"
                        :fraction="discountType === 'percentage'"
                        label="Nilai Diskon"
                        :max="maxDiscount"
                        :prefix="discountPrefix"
                        :suffix="discountSuffix"
                    />

                    <div class="col-span-full flex flex-row items-center justify-end">
                        <PV.Button class="shadow-sm w-full md:w-auto" :form="formId" severity="secondary" type="submit">
                            <BasketAddIcon />
                            <span class="font-semibold">Tambahkan Produk</span>
                        </PV.Button>
                    </div>
                </div>
            </template>
        </FormHook>

        <div class="w-full overflow-x-auto">
            <table class="rns-table-items mt-6">
                <thead>
                    <tr>
                        <th></th>
                        <th class="text-start">Nama Produk</th>
                        <th>Jumlah</th>
                        <th class="text-end">Harga Satuan</th>
                        <th class="text-end">Diskon</th>
                        <th class="text-end">Pajak</th>
                        <th class="text-end">Total Harga</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, index) in model">
                        <td v-if="state.editMode" class="p-0!">
                            <div class="inline-flex flex-row items-center justify-center w-full">
                                <TrashIcon
                                    class="hover:cursor-pointer text-red-500 mt-2 mb-2"
                                    font-size="18"
                                    @click="() => onRemove(index)"
                                />
                            </div>
                        </td>
                        <td :colspan="state.editMode ? 1 : 2">{{ item.name }}</td>
                        <td class="text-center">{{ item.quantity }}</td>
                        <td class="text-end">{{ formatRupiah(item.price) }}</td>
                        <td class="text-end">{{ formatRupiah(item.discount) }}</td>
                        <td class="text-end">{{ item.tax ?? 0 }} %</td>
                        <td class="text-end">{{ formatRupiah(item.total) }}</td>
                    </tr>
                    <tr class="rns-table-total">
                        <td colspan="6">Subtotal</td>
                        <td class="text-end">{{ formatRupiah(invoiceNumbers.sumTotal) }}</td>
                    </tr>
                    <tr v-if="invoiceNumbers.sumDiscount > 0" class="rns-table-total">
                        <td colspan="6">Total Diskon</td>
                        <td class="text-end font-semibold">- {{ formatRupiah(invoiceNumbers.sumDiscount) }}</td>
                    </tr>
                    <tr v-if="invoiceNumbers.discountSub > 0" class="rns-table-total">
                        <td colspan="6">Diskon Tagihan</td>
                        <td class="text-end font-semibold">- {{ formatRupiah(invoiceNumbers.discountSub) }}</td>
                    </tr>
                    <tr v-if="invoiceNumbers.taxAddition > 0" class="rns-table-total">
                        <td colspan="6">Total Pajak</td>
                        <td class="text-end">{{ formatRupiah(invoiceNumbers.taxAddition) }}</td>
                    </tr>
                    <tr class="bg-gray-800 text-white">
                        <td class="text-end font-bold" colspan="6">Grand Total</td>
                        <td class="text-end">{{ formatRupiah(invoiceNumbers.grandTotal) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="state.hasError" class="py-4">
            <FormMessage :state="state" />
        </div>
    </FormPanel>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-table-items {
    @apply shadow-md border border-gray-800 w-full;
}

.rns-table-items th,
.rns-table-items td {
    @apply px-4 py-2;
}

.rns-table-items th {
    @apply font-semibold;
}

.rns-table-total td:first-child {
    @apply text-end font-medium;
}

.rns-table-total td {
    @apply bg-gray-200;
}
</style>
